import { api, type Level, type Metric, type ScreenResult } from "./api.ts";
import { fromOf, inPurchaseRateState, taxRuleNote, usesPurchaseRule } from "../shared/regions.ts";
import { fmt, LEVEL_LABEL } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import { metricSelect, pager, placeSearch, sortHeader } from "./widgets.ts";
import {
  activeRank, defaultRankDir, describeScreen, emptyScreen, inputValue, OUTLOOK_LABEL, parseInput, parseScreen, PRESETS, RANK_MAX,
  screenParams, unitHint, type Outlook, type Screen,
} from "./screen.ts";

export type ScreenLevel = Exclude<Level, "us">;

export interface ScreenerDeps {
  metrics: Map<string, Metric>;
  categories: string[];
  getLevel(): ScreenLevel;
  setLevel(l: ScreenLevel): void;
  getScreen(): Screen | null;
  /** the screen changed (main mirrors it in the URL and on the map) */
  setScreen(s: Screen | null): void;
  /** the map's metric, shown as an extra column */
  getMapMetric(): Metric;
  onRowClick(geoId: string): void;
  /** a row is hovered (its pin on the map lights up), or none */
  onRowHover(geoId: string | null): void;
  /** the docked list's rows, numbered from `first`, to pin on the map; null clears the pins */
  onPins(level: ScreenLevel, rows: Array<{ geo_id: string; label: string }> | null, first: number): void;
  onShowMap(): void;
  /** the docked list's "Edit filters": back to the full screener */
  onEdit(): void;
  /** a budget idea or filter was picked before My budget is set: open its form */
  onNeedBudget(): void;
  onClose(): void;
}

const PAGE = 100;
/** the list docked beside the map: short enough that every row has its numbered pin */
const DOCKED_PAGE = 25;
const LEVELS: ScreenLevel[] = ["state", "metro", "county", "city", "zip"];
/** What "Inside" can name for each level (metros carry no state). */
const CONTAINERS: Record<ScreenLevel, Level[]> = { zip: ["state", "metro", "county"], city: ["state", "metro", "county"], county: ["state", "metro"], metro: [], state: [] };
const SAVED_KEY = "monitusre.screens.v1";

export interface SavedScreen { name: string; level: ScreenLevel; params: string }

function loadSaved(): SavedScreen[] {
  try {
    const v = JSON.parse(localStorage.getItem(SAVED_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((s) => s && typeof s.name === "string" && typeof s.params === "string") : [];
  } catch {
    return [];
  }
}
function storeSaved(list: SavedScreen[]) {
  try {
    localStorage.setItem(SAVED_KEY, JSON.stringify(list));
  } catch { /* private window: saving just doesn't stick */ }
}

/** Combine filters (metric thresholds, outlook, place) into a list of areas; the matches are also marked on the map. */
export class ScreenerView {
  private offset = 0;
  private seq = 0;
  private abort?: AbortController;
  private timer?: ReturnType<typeof setTimeout>;
  private form = el("div", { class: "screen-form" });
  private results = el("div", { class: "screen-results" });
  private draft: Screen = emptyScreen();
  private isDocked = false;

  constructor(private readonly root: HTMLElement, private readonly d: ScreenerDeps) {}

  get open() {
    return !this.root.hidden;
  }

  /** the results list sits beside the map (Show on map) instead of over it */
  get docked() {
    return this.open && this.isDocked;
  }

  private get page() {
    return this.isDocked ? DOCKED_PAGE : PAGE;
  }

  show() {
    if (this.isDocked) this.d.onPins(this.d.getLevel(), null, 1);
    this.isDocked = false;
    this.root.classList.remove("docked");
    this.root.hidden = false;
    this.draft = structuredClone(this.d.getScreen() ?? emptyScreen());
    this.offset = 0;
    this.root.replaceChildren(
      el("header", { class: "table-head" },
        el("div", {},
          el("h2", {}, "Screener"),
          el("div", { class: "muted small" }, "Areas that pass every filter, from each data point's latest month")),
        el("div", { class: "panel-actions" },
          el("button", { class: "btn small", onclick: () => this.d.onShowMap() }, "Show on map"),
          el("button", { class: "btn icon", "aria-label": "Close screener", onclick: () => this.d.onClose() }, "✕"))),
      el("div", { class: "screen-body" }, this.form, this.results));
    this.renderForm();
    void this.renderResults();
  }

  /** Show on map: just the results, beside the map, each row numbered like its pin. */
  dock() {
    this.isDocked = true;
    this.root.classList.add("docked");
    this.root.hidden = false;
    this.draft = structuredClone(this.d.getScreen() ?? emptyScreen());
    this.offset = 0;
    this.root.replaceChildren(
      el("header", { class: "table-head" },
        el("h2", {}, "Screener results"),
        el("div", { class: "panel-actions" },
          el("button", { class: "btn small", onclick: () => this.d.onEdit() }, "Edit filters"),
          el("button", { class: "btn icon", "aria-label": "Close screener", onclick: () => this.d.onClose() }, "✕"))),
      this.results);
    void this.renderResults();
  }

  hide() {
    if (this.isDocked && this.open) this.d.onPins(this.d.getLevel(), null, 1);
    this.isDocked = false;
    this.root.classList.remove("docked");
    this.root.hidden = true;
  }

  /** light up a row (its pin or area is hovered on the map), or none */
  highlight(geoId: string | null) {
    for (const r of this.results.querySelectorAll<HTMLElement>("tbody tr")) r.classList.toggle("focus", r.dataset.geo === geoId);
  }

  /** The level or the screen changed from outside (level bar, a link, Back). */
  refresh() {
    if (!this.open) return;
    this.draft = structuredClone(this.d.getScreen() ?? emptyScreen());
    this.offset = 0;
    if (!this.isDocked) this.renderForm();
    void this.renderResults();
  }

  /** A form edit: publish the screen and re-run the results (debounced while typing). */
  private changed(rerenderForm = false, delay = 0) {
    if (rerenderForm) this.renderForm();
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.offset = 0;
      const s = this.draft;
      this.d.setScreen(s.filters.length || s.rank.length || s.outlook || s.where ? structuredClone(s) : null);
      void this.renderResults();
    }, delay);
  }

  private renderForm() {
    const s = this.draft;
    const level = this.d.getLevel();
    const has = (id: string) => !!this.d.metrics.get(id)?.available[level];

    const pickMetric = (value: string, onChange: (id: string) => void) => metricSelect({ metrics: this.d.metrics.values(), value, onChange,
      cls: "screen-metric", label: "Data point", categories: this.d.categories, unavailable: (m) => (has(m.id) ? null : `no ${LEVEL_LABEL[level]} data`) });

    const filterRows = s.filters.map((f, i) => {
      const m = this.d.metrics.get(f.metric)!;
      const input = el("input", {
        class: "screen-value", type: "text", inputmode: "decimal", value: inputValue(m, f.value), placeholder: "value",
        "aria-label": `${m.label} ${f.op === "min" ? "at least" : "at most"}`,
        oninput: (e: Event) => {
          const t = e.target as HTMLInputElement;
          f.value = parseInput(m, t.value);
          t.classList.toggle("bad", t.value.trim() !== "" && f.value == null);
          this.changed(false, 350);
        },
      });
      return el("div", { class: "screen-filter" },
        pickMetric(f.metric, (id) => {
          const old = m;
          const nm = this.d.metrics.get(id)!;
          // keep the number the user sees when the unit kind stays the same
          f.value = (old.unit === "pct" || old.unit === "pct1") === (nm.unit === "pct" || nm.unit === "pct1") ? f.value : null;
          f.metric = id;
          this.changed(true);
        }),
        el("div", { class: "screen-filter-row" },
          el("select", { "aria-label": "Comparison", onchange: (e: Event) => { f.op = (e.target as HTMLSelectElement).value as "min" | "max"; this.changed(); } },
            el("option", { value: "min", selected: f.op === "min" }, "at least"),
            el("option", { value: "max", selected: f.op === "max" }, "at most")),
          input,
          el("span", { class: "muted small screen-unit" }, unitHint(m)),
          el("button", { class: "btn icon", "aria-label": `Remove ${m.label} filter`, onclick: () => { s.filters.splice(i, 1); this.changed(true); } }, "✕")),
        has(f.metric) ? null : el("div", { class: "muted small" },
          m.personal && !Object.keys(m.available).length
            ? el("span", {}, "Needs your budget: ", el("button", { class: "link", onclick: () => this.d.onNeedBudget() }, "add your income"))
            : `No ${LEVEL_LABEL[level]} data: nothing passes this filter`));
    });

    const addFilter = () => {
      const used = new Set(s.filters.map((f) => f.metric));
      const id = ["home_value", "cap_rate", "buyer_leverage", "overvalued_pct", "forecast_pct", "population_growth"].find((x) => !used.has(x) && has(x))
        ?? [...this.d.metrics.keys()].find((x) => !used.has(x) && has(x))!;
      const m = this.d.metrics.get(id)!;
      s.filters.push({ metric: id, op: m.higher === "good" ? "min" : "max", value: null });
      this.changed(true);
    };

    // Rank by: 2-3 data points combined (one row alone waits for a second)
    const rankRows = s.rank.map((r, i) => {
      const m = this.d.metrics.get(r.metric)!;
      return el("div", { class: "screen-filter" },
        pickMetric(r.metric, (id) => {
          if (s.rank.some((x, j) => j !== i && x.metric === id)) { this.renderForm(); return; } // already ranked
          r.metric = id;
          r.dir = defaultRankDir(this.d.metrics.get(id)!);
          this.changed(true);
        }),
        el("div", { class: "screen-filter-row" },
          el("select", { "aria-label": `${m.label}: which end is best`, onchange: (e: Event) => { r.dir = (e.target as HTMLSelectElement).value as "high" | "low"; this.changed(); } },
            el("option", { value: "high", selected: r.dir === "high" }, "higher is better"),
            el("option", { value: "low", selected: r.dir === "low" }, "lower is better")),
          el("button", { class: "btn icon", "aria-label": `Remove ${m.label} from the ranking`, onclick: () => { s.rank.splice(i, 1); this.changed(true); } }, "✕")),
        has(r.metric) ? null : el("div", { class: "muted small" }, `No ${LEVEL_LABEL[level]} data: nothing can be ranked by it`));
    });
    const addRank = () => {
      const used = new Set(s.rank.map((r) => r.metric));
      const id = ["rent_yoy", "home_value_yoy", "cap_rate", "forecast_pct", "population_growth"].find((x) => !used.has(x) && has(x))
        ?? [...this.d.metrics.keys()].find((x) => !used.has(x) && has(x))!;
      s.rank.push({ metric: id, dir: defaultRankDir(this.d.metrics.get(id)!) });
      this.changed(true);
    };

    const outlook = el("select", { "aria-label": "1-year outlook", onchange: (e: Event) => { s.outlook = ((e.target as HTMLSelectElement).value || null) as Outlook | null; this.changed(); } },
      el("option", { value: "", selected: !s.outlook }, "Any"),
      ...(Object.keys(OUTLOOK_LABEL) as Outlook[]).map((o) => el("option", { value: o, selected: s.outlook === o }, OUTLOOK_LABEL[o])));

    this.form.replaceChildren(...nodes(
      el("div", { class: "screen-levels segmented", role: "radiogroup", "aria-label": "Level" },
        // only levels that have data (a hidden level, e.g. city, has none: metrics.yaml hidden_levels:)
        ...LEVELS.filter((l) => [...this.d.metrics.values()].some((m) => m.available[l])).map((l) => el("button", { role: "radio", class: `seg ${l === level ? "active" : ""}`, "aria-checked": String(l === level), onclick: () => this.d.setLevel(l) }, LEVEL_LABEL[l]!))),
      el("h4", { class: "side-head" }, "Filters"),
      ...filterRows,
      el("button", { class: "btn small", onclick: addFilter }, "+ Add filter"),
      el("h4", { class: "side-head" }, "Rank by"),
      ...rankRows,
      s.rank.length === 1 ? el("p", { class: "muted small" }, "Add a second data point to rank by both together.")
        : s.rank.length === 0 ? el("p", { class: "muted small" }, "Up to 3 data points together: each area's percentile on each, averaged.") : null,
      s.rank.length < RANK_MAX ? el("button", { class: "btn small", onclick: addRank }, s.rank.length ? "+ Add data point" : "+ Rank by data points") : null,
      el("h4", { class: "side-head" }, "1-year outlook"),
      outlook,
      el("h4", { class: "side-head" }, "Where"),
      this.whereEditor(level),
      el("h4", { class: "side-head" }, "Start from an idea"),
      // an idea whose data points this level lacks isn't offered; a budget idea before My budget is set opens its form
      el("div", { class: "screen-presets" }, ...PRESETS.filter((p) => (p.screen.filters ?? []).every((f) => has(f.metric) || this.needsBudget(f.metric))
        && (p.screen.rank ?? []).every((r) => has(r.metric))).map((p) => el("button", { class: "qchip", onclick: () => {
        if ((p.screen.filters ?? []).some((f) => this.needsBudget(f.metric))) { this.d.onNeedBudget(); return; }
        this.draft = { ...emptyScreen(), ...structuredClone(p.screen), where: s.where };
        this.changed(true);
      } }, p.label))),
      this.savedEditor(level),
    ));
  }

  /** a budget metric with no data yet (My budget isn't set) */
  private needsBudget(id: string) {
    const m = this.d.metrics.get(id);
    return !!m?.personal && !Object.keys(m.available).length;
  }

  /** Anywhere / inside a place / within N miles of a place, with a search box for the place. */
  private whereEditor(level: ScreenLevel) {
    const s = this.draft;
    const canIn = CONTAINERS[level].length > 0;
    const mode = s.where?.mode ?? "any";
    const wrap = el("div", { class: "screen-where" });
    const setMode = (m: "any" | "in" | "near") => {
      if (m === "any") s.where = null;
      else if (s.where) s.where = { ...s.where, mode: m };
      else { pending = m; render(); return; }
      if (s.where?.mode === "in" && !CONTAINERS[level].includes(s.where.geo_id.split(":")[0] as Level)) s.where = null;
      this.changed(true);
    };
    let pending: "in" | "near" | null = null;
    const render = () => {
      const cur = pending ?? mode;
      const { input: search, list } = placeSearch({ placeholder: cur === "in" ? "State, metro or county" : "Any place: ZIP, city, county…", label: "Place", listClass: "screen-search",
        keep: (r) => r.level !== "us" && (cur === "near" || CONTAINERS[level].includes(r.level)),
        onPick: (r) => {
          s.where = { mode: cur as "in" | "near", geo_id: r.geo_id, name: r.level === "zip" ? `ZIP ${r.code}` : r.name, miles: s.where?.miles ?? 25 };
          pending = null;
          this.changed(true);
        } });
      wrap.replaceChildren(...nodes(
        el("select", { "aria-label": "Where", onchange: (e: Event) => setMode((e.target as HTMLSelectElement).value as "any" | "in" | "near") },
          el("option", { value: "any", selected: cur === "any" }, "Anywhere"),
          el("option", { value: "in", selected: cur === "in", disabled: !canIn }, canIn ? "Inside…" : "Inside… (not for this level)"),
          el("option", { value: "near", selected: cur === "near" }, "Within … miles of")),
        cur === "near" ? el("label", { class: "screen-miles" },
          el("input", { type: "number", min: 1, max: 500, step: 1, value: String(s.where?.miles ?? 25), "aria-label": "Miles",
            oninput: (e: Event) => {
              const v = Number((e.target as HTMLInputElement).value);
              if (s.where && Number.isFinite(v) && v >= 1 && v <= 500) { s.where.miles = v; this.changed(false, 350); }
            } }), " miles") : null,
        cur !== "any" && s.where && !pending ? el("div", { class: "screen-place" },
          el("strong", {}, s.where.name),
          el("button", { class: "link", onclick: () => { pending = s.where!.mode; s.where = null; this.changed(false); render(); } }, " change")) : null,
        cur !== "any" && (!s.where || pending) ? el("div", { class: "search" }, search, list) : null));
      if (cur !== "any" && (!s.where || pending)) queueMicrotask(() => search.focus());
    };
    render();
    return wrap;
  }

  private savedEditor(level: ScreenLevel) {
    const list = loadSaved();
    const name = el("input", { type: "text", placeholder: "Name this screen", "aria-label": "Screen name", maxlength: 60 });
    const save = () => {
      const n = name.value.trim();
      if (!n) { name.focus(); return; }
      const params = screenParams(this.draft).toString();
      storeSaved([{ name: n, level, params }, ...list.filter((x) => x.name !== n)].slice(0, 30));
      this.renderForm();
    };
    name.addEventListener("keydown", (e) => { if (e.key === "Enter") save(); });
    const isMetric = (id: string) => this.d.metrics.has(id);
    return el("section", { class: "screen-saved" },
      el("h4", { class: "side-head" }, "Saved screens"),
      el("div", { class: "screen-save-row" }, name, el("button", { class: "btn small", onclick: save }, "Save")),
      list.length
        ? el("ul", {}, ...list.map((x) => {
            const sc = parseScreen(new URLSearchParams(x.params), isMetric);
            return el("li", {},
              el("button", { class: "link", title: sc ? describeScreen(sc, this.d.metrics, (m, v) => fmt(m, v)) : "", onclick: () => {
                this.draft = sc ?? emptyScreen();
                if (x.level === this.d.getLevel()) { this.changed(true); return; }
                this.d.setScreen(sc);
                this.d.setLevel(x.level); // re-renders through refresh()
              } }, x.name),
              el("span", { class: "tag" }, LEVEL_LABEL[x.level]!),
              el("button", { class: "btn icon", "aria-label": `Delete ${x.name}`, onclick: () => { storeSaved(list.filter((y) => y !== x)); this.renderForm(); } }, "✕"));
          }))
        : el("p", { class: "muted small" }, "Saved in this browser. Copy the page link to share a screen."));
  }

  private async renderResults() {
    const seq = ++this.seq;
    this.abort?.abort();
    this.abort = new AbortController();
    const level = this.d.getLevel();
    const s = this.draft;
    const mapMetric = this.d.getMapMetric();
    const q = screenParams(s, true);
    const extra = [...s.filters, ...activeRank(s)].some((f) => f.metric === mapMetric.id) || !mapMetric.available[level] ? [] : [mapMetric.id];
    this.results.classList.add("loading");
    let res: ScreenResult;
    try {
      res = await api.screen(level, q, { cols: extra, limit: this.page, offset: this.offset }, this.abort.signal);
    } catch (e) {
      if (seq !== this.seq || (e as Error).name === "AbortError") return;
      this.results.classList.remove("loading");
      this.results.replaceChildren(el("div", { class: "panel-error" }, (e as Error).message));
      return;
    }
    if (seq !== this.seq) return;
    this.results.classList.remove("loading");

    const sortBy = (key: string) => {
      if (res.sort === key) s.dir = res.dir === "asc" ? "desc" : "asc";
      else { s.sort = key; s.dir = null; }
      this.changed();
    };
    const th = (key: string, label: string, num = true) => sortHeader(label, { active: res.sort === key, dir: res.dir, num, onSort: () => sortBy(key) });
    const near = !!res.near;
    const docked = this.isDocked;
    // docked, the list is narrow: the place, its sort column (or the first filter's) and the chance of a rise
    const combined = res.combined;
    const keyCol = res.columns.includes(res.sort) ? res.sort : combined ? null : res.columns[0];
    const cols = docked ? (keyCol ? [keyCol] : []) : res.columns;
    const placeName = (r: ScreenResult["rows"][number]) => (level === "zip" ? `${r.geo_id.slice(4)} · ${r.name}` : r.name);
    const head = el("tr", {},
      el("th", { class: "num" }, "#"),
      th("name", LEVEL_LABEL[level]!, false),
      near ? th("distance", "Miles") : null,
      combined && (!docked || res.sort === "score" || !keyCol) ? th("score", "Combined") : null,
      ...cols.map((c) => th(c, this.d.metrics.get(c)!.label)),
      th("p_rise", docked ? "Chance up" : "Chance of a rise"));
    const body = el("tbody", {}, ...res.rows.map((r, i) =>
      el("tr", { tabindex: 0, "data-geo": r.geo_id, onclick: () => this.d.onRowClick(r.geo_id),
        onkeydown: (e: Event) => { if ((e as KeyboardEvent).key === "Enter") this.d.onRowClick(r.geo_id); },
        onmouseenter: () => this.d.onRowHover(r.geo_id), onmouseleave: () => this.d.onRowHover(null) },
        el("td", { class: "num muted" }, docked ? el("span", { class: "ask-num" }, String(this.offset + i + 1)) : String(this.offset + i + 1)),
        el("td", { class: docked ? "screen-place-cell" : "" }, placeName(r)),
        near ? el("td", { class: "num" }, r.distance == null ? "–" : r.distance.toFixed(1)) : null,
        combined && (!docked || res.sort === "score" || !keyCol) ? el("td", { class: "num", title: "Average percentile across the ranked data points (100 = best on all)" }, r.score == null ? "–" : String(Math.round(r.score * 100))) : null,
        ...cols.map((c) => el("td", { class: "num" }, fmt(this.d.metrics.get(c)!, r.values[c] ?? null))),
        el("td", { class: "num" }, r.p_rise == null ? "–" : `${Math.round(r.p_rise * 100)}%`))));
    if (docked) this.d.onPins(level, res.rows.map((r) => ({ geo_id: r.geo_id, label: placeName(r) })), this.offset + 1);

    const size = this.page;
    const pages = Math.ceil(res.total / size);
    const page = Math.floor(this.offset / size) + 1;
    const active = s.filters.some((f) => f.value != null) || activeRank(s).length || s.outlook || s.where;
    // a ranking only counts areas with every ranked value: say how many it left out, and for want of what
    const leftOut = combined && combined.left_out > 0
      ? `${combined.left_out.toLocaleString()} of ${combined.passed.toLocaleString()} left out for missing data: ${Object.entries(combined.missing)
          .filter(([, n]) => n > 0).map(([id, n]) => `${n.toLocaleString()} without ${this.d.metrics.get(id)?.label ?? id}`).join(", ")}`
        + (level === "zip" && combined.rent ? ". Zillow publishes rent for about a third of ZIPs, mostly larger, urban ones." : "")
      : null;
    this.results.replaceChildren(...nodes(
      el("div", { class: "screen-summary" },
        el("div", {},
          el("strong", {}, `${res.total.toLocaleString()} ${res.total === 1 ? "area" : "areas"}`),
          el("span", { class: "muted small" }, active ? ` · ${describeScreen(s, this.d.metrics, (m, v) => fmt(m, v))}` : ` · every ${LEVEL_LABEL[level]} (add a filter)`)),
        docked ? null : el("a", { class: "btn small", href: api.screenCsvUrl(level, q, extra), download: "" }, "Export CSV")),
      leftOut ? el("p", { class: "muted small screen-dock-note" }, leftOut) : null,
      // a payment compared across states mixes two property-tax bases (shared/regions.ts)
      [...s.filters, ...activeRank(s)].some((f) => usesPurchaseRule(f.metric, fromOf((id) => this.d.metrics.get(id))))
        && level !== "city" && inPurchaseRateState(s.where?.mode === "in" ? [s.where.geo_id] : []) === null
        ? el("p", { class: "muted small screen-dock-note" }, taxRuleNote(null)) : null,
      docked && res.rows.length ? el("p", { class: "muted small screen-dock-note" },
        `Numbered on the map${pages > 1 ? ` (this page's ${res.rows.length})` : ""}; every match stays colored, the rest fade. Click a row to open the place.`) : null,
      res.rows.length
        ? el("div", { class: "table-scroll" }, el("table", { class: "rank" }, el("thead", {}, head), body))
        : el("p", { class: "muted screen-empty" }, "No area passes every filter. Loosen one, or widen the place."),
      pager(page, pages, (step) => { this.offset += step * size; void this.renderResults(); })));
  }
}
